import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { reportsAPI, customersAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, FileText, AlertCircle } from 'lucide-react';
import { DateRangeBar, ExportBar, KPICard, ReportLoader, ReportEmpty, ReportError, useCurrency, downloadCsv } from '../../components/reports/ReportKit';
import SendEmailButton from '../../components/email/SendEmailButton';
import { printReportDocument } from '../../lib/printReport';

const CustomerStatementReport = () => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    const fmtMoney = useCurrency();
    const [customerId, setCustomerId] = useState('');
    const [filters, setFilters] = useState({ start: null, end: null });
    const [customers, setCustomers] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: c } = await customersAPI.getAll();
                setCustomers(c || []);
                // pre-fill date range: this year so far
                const now = new Date();
                setFilters({ start: new Date(now.getFullYear(), 0, 1), end: now });
            } catch { /* non-fatal */ }
        })();
    }, []);

    const load = useCallback(async () => {
        if (!customerId) return;
        setLoading(true);
        setError(null);
        try {
            const params = { customer_id: customerId };
            if (filters.start) params.start_date = format(filters.start, 'yyyy-MM-dd');
            if (filters.end) params.end_date = format(filters.end, 'yyyy-MM-dd');
            const { data: res } = await reportsAPI.customerStatement(params);
            setData(res);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [customerId, filters, t]);

    useEffect(() => { if (customerId && filters.start && filters.end) load(); }, [load, customerId, filters.start, filters.end]);

    const setPreset = (days) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) { start.setHours(0, 0, 0, 0); } else start.setDate(start.getDate() - days);
        setFilters({ start, end });
    };

    const exportCsv = () => {
        if (!data?.entries?.length) return toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
        const columns = [
            { key: 'date', label: t('entities:reports.columns.date') },
            { key: 'type', label: t('entities:reports.statement.column_type', { defaultValue: 'Type' }) },
            { key: 'reference', label: t('entities:reports.columns.invoice') },
            { key: 'description', label: t('entities:reports.statement.column_description', { defaultValue: 'Description' }) },
            { key: 'debit', label: t('entities:reports.statement.column_debit', { defaultValue: 'Debit' }) },
            { key: 'credit', label: t('entities:reports.statement.column_credit', { defaultValue: 'Credit' }) },
            { key: 'balance', label: t('entities:reports.statement.column_balance', { defaultValue: 'Balance' }) },
            { key: 'due_date', label: t('entities:reports.statement.column_due_date', { defaultValue: 'Due Date' }) },
        ];
        const company = data.customer?.company_name?.replace(/\s+/g, '_') || 'customer';
        downloadCsv(`statement-${company}-${format(filters.start, 'yyyy-MM-dd')}_${format(filters.end, 'yyyy-MM-dd')}.csv`, columns, data.entries);
        toast.success(t('entities:reports.toasts.exported'));
    };

    /**
     * Custom print: opens a branded PDF with the new tabular layout
     * (Date | Facture N° | Echéance | Mont. HT | TVA | TTC | Cumulé | Règlement)
     * and the "Reste à régler" / "Solde de la Période" callouts.
     */
    const handlePrint = () => {
        if (!data?.entries?.length) {
            toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
            return;
        }
        const lang = data.customer?.document_language || i18n.language || 'fr';
        const moneyTxt = (v) => fmtMoney(v).replace(/\u00a0/g, ' ');
        const dash = '—';

        // Keep only invoice rows in the table; payments fold into each invoice's "Règlement" column.
        const invoiceRows = data.entries.filter((e) => e.type !== 'payment');

        const tableRows = invoiceRows.map((e) => `
            <tr class="${e.is_overdue ? 'overdue' : ''}">
                <td>${e.date || dash}</td>
                <td style="font-family:'Courier New',monospace;">${e.reference || dash}</td>
                <td>${e.due_date || dash}</td>
                <td class="num">${moneyTxt(e.ht || 0)}</td>
                <td class="num">${moneyTxt(e.vat || 0)}</td>
                <td class="num">${moneyTxt(e.ttc || e.debit || 0)}</td>
                <td class="num" style="font-weight:600;">${moneyTxt(e.balance || 0)}</td>
                <td class="num" style="color:#2A9D8F;">${e.paid ? moneyTxt(e.paid) : dash}</td>
            </tr>
        `).join('');

        const totals = {
            ht: invoiceRows.reduce((sum, e) => sum + (e.ht || 0), 0),
            vat: invoiceRows.reduce((sum, e) => sum + (e.vat || 0), 0),
            ttc: invoiceRows.reduce((sum, e) => sum + (e.ttc || e.debit || 0), 0),
            paid: invoiceRows.reduce((sum, e) => sum + (e.paid || 0), 0),
        };

        const periodBalance = s.invoices_total - s.payments_total;

        const c = data.customer || {};
        // Compose a single-line address from structured billing_address > legacy company_address.
        const addrPieces = c.billing_address && (c.billing_address.street || c.billing_address.city)
            ? [c.billing_address.street, c.billing_address.complement, [c.billing_address.postal_code, c.billing_address.city].filter(Boolean).join(' '), c.billing_address.country].filter(Boolean).join(', ')
            : (c.company_address || '');
        const periodLabel = (filters.start && filters.end)
            ? `${format(filters.start, 'dd MMM yyyy')} — ${format(filters.end, 'dd MMM yyyy')}`
            : '';

        const metaHtml = `
            <div class="report-meta">
                <div class="block">
                    <div class="label">${lang.startsWith('fr') ? 'Client' : 'Customer'}</div>
                    <div class="v">${c.company_name || dash}</div>
                    ${addrPieces ? `<div class="meta-line">${addrPieces}</div>` : ''}
                    ${c.contact_person ? `<div class="meta-line">${c.contact_person}</div>` : ''}
                    ${c.mobile_number || c.email_address ? `<div class="meta-line">${c.mobile_number || ''}${c.mobile_number && c.email_address ? ' · ' : ''}${c.email_address || ''}</div>` : ''}
                    <div class="id-grid">
                        <div class="cell"><span class="k">Client N°</span><span class="v">${c.customer_code || dash}</span></div>
                        <div class="cell"><span class="k">SIRET N°</span><span class="v">${c.siret || dash}</span></div>
                        <div class="cell"><span class="k">TVA N°</span><span class="v">${c.tva || dash}</span></div>
                    </div>
                </div>
                <div class="block right">
                    <div class="label">${lang.startsWith('fr') ? 'Période du relevé' : 'Statement period'}</div>
                    <div class="v" style="font-size:11.5px;">${periodLabel}</div>
                    <div class="meta-line" style="color:#888;">
                        ${lang.startsWith('fr') ? 'Échéance contractuelle' : 'Payment terms'}: ${c.overdue_threshold_days || 30} ${lang.startsWith('fr') ? 'jours' : 'days'}
                    </div>
                </div>
            </div>

            <div class="kpi-strip">
                <div class="kpi"><span class="k">${lang.startsWith('fr') ? "Solde d'ouverture" : 'Opening balance'}</span><span class="v">${moneyTxt(s.opening_balance)}</span></div>
                <div class="kpi accent"><span class="k">${lang.startsWith('fr') ? 'Facturé' : 'Invoiced'}</span><span class="v">${moneyTxt(s.invoices_total)}</span></div>
                <div class="kpi success"><span class="k">${lang.startsWith('fr') ? 'Réglé' : 'Paid'}</span><span class="v">${moneyTxt(s.payments_total)}</span></div>
                <div class="kpi danger"><span class="k">${lang.startsWith('fr') ? 'En retard' : 'Overdue'}</span><span class="v">${moneyTxt(s.overdue_amount)}</span></div>
            </div>

            <div class="callouts">
                <div class="callout amber">
                    <span class="k">${lang.startsWith('fr') ? 'Reste à régler' : 'Outstanding to pay'}</span>
                    <div class="v">${moneyTxt(s.closing_balance)}</div>
                </div>
                <div class="callout">
                    <span class="k">${lang.startsWith('fr') ? 'Solde de la période' : 'Period balance'}</span>
                    <div class="v">${moneyTxt(periodBalance)}</div>
                </div>
            </div>
        `;

        const contentHtml = `
            <table class="report-table">
                <thead>
                    <tr>
                        <th>${lang.startsWith('fr') ? 'Date' : 'Date'}</th>
                        <th>${lang.startsWith('fr') ? 'Facture N°' : 'Invoice N°'}</th>
                        <th>${lang.startsWith('fr') ? 'Échéance' : 'Due date'}</th>
                        <th class="num">${lang.startsWith('fr') ? 'Mont. H.T.' : 'Net HT'}</th>
                        <th class="num">TVA</th>
                        <th class="num">TTC</th>
                        <th class="num">${lang.startsWith('fr') ? 'Cumulé' : 'Running'}</th>
                        <th class="num">${lang.startsWith('fr') ? 'Règlement' : 'Payment'}</th>
                    </tr>
                </thead>
                <tbody>${tableRows}</tbody>
                <tfoot>
                    <tr>
                        <td colspan="3" style="text-transform:uppercase; letter-spacing:1px;">
                            ${lang.startsWith('fr') ? 'Total' : 'Total'}
                        </td>
                        <td class="num">${moneyTxt(totals.ht)}</td>
                        <td class="num">${moneyTxt(totals.vat)}</td>
                        <td class="num">${moneyTxt(totals.ttc)}</td>
                        <td class="num">${moneyTxt(s.closing_balance)}</td>
                        <td class="num">${moneyTxt(totals.paid)}</td>
                    </tr>
                </tfoot>
            </table>
        `;

        printReportDocument({
            title: lang.startsWith('fr') ? 'Relevé de Compte Client' : 'Customer Statement',
            subtitle: periodLabel,
            contentHtml,
            metaHtml,
            language: lang,
        });
    };

    const s = data?.summary;

    return (
        <div data-testid="customer-statement-page" className="space-y-6 animate-fadeIn">
            <div className="flex items-center justify-between no-print">
                <div>
                    <Link to="/reports" className="text-xs text-gray-500 hover:text-[#C08478] flex items-center gap-1 mb-2">
                        <ArrowLeft size={12} /> {t('entities:reports.hub.title')}
                    </Link>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:reports.hub.cards.customer_statement.title')}</h1>
                    <p className="text-gray-500 text-sm mt-1">{t('entities:reports.hub.cards.customer_statement.description')}</p>
                </div>
                <div className="flex items-center gap-2">
                    {customerId && (
                        <SendEmailButton
                            testId="email-statement"
                            label={t('entities:reports.statement.email_statement', { defaultValue: 'Email Statement' })}
                            title={t('entities:reports.statement.email_statement', { defaultValue: 'Email Statement' })}
                            description={t('entities:reports.statement.email_statement_hint', { defaultValue: "Send this statement to the customer's accountant or contact." })}
                            requireRecipient
                            disabled={!data?.entries?.length}
                            defaultTo={data?.customer?.email_address || ''}
                            onSend={async (to) => {
                                await reportsAPI.emailCustomerStatement({
                                    customer_id: customerId,
                                    to,
                                    start_date: filters.start ? format(filters.start, 'yyyy-MM-dd') : undefined,
                                    end_date: filters.end ? format(filters.end, 'yyyy-MM-dd') : undefined,
                                });
                            }}
                        />
                    )}
                    <ExportBar onCsv={exportCsv} onPrint={handlePrint} disabled={!data?.entries?.length} />
                </div>
            </div>

            <DateRangeBar
                start={filters.start}
                end={filters.end}
                onChange={({ start, end }) => setFilters({ start, end })}
                onPreset={setPreset}
            >
                <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger className="h-9 w-72" data-testid="statement-customer">
                        <SelectValue placeholder={t('entities:reports.statement.select_customer', { defaultValue: 'Select a customer...' })} />
                    </SelectTrigger>
                    <SelectContent>
                        {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                <span className="font-mono text-xs text-[#C08478] mr-2">{c.customer_code}</span>
                                {c.company_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </DateRangeBar>

            {!customerId && (
                <ReportEmpty icon={FileText} message={t('entities:reports.statement.pick_customer_hint', { defaultValue: 'Select a customer to generate their statement.' })} />
            )}

            {customerId && loading && <ReportLoader />}
            {customerId && error && !loading && <ReportError message={error} onRetry={load} />}

            {customerId && !loading && !error && data && (
                <>
                    {/* Letterhead */}
                    <Card className="border-0 shadow-sm">
                        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-[10px] uppercase tracking-[2px] text-[#C08478] font-semibold">UNIVERS COSMETIC</p>
                                <h2 className="text-lg font-bold text-[#1A1A1A]">{data.customer.company_name}</h2>
                                <p className="text-xs text-gray-500 font-mono">{data.customer.customer_code}</p>
                                {data.customer.contact_person && <p className="text-xs text-gray-500 mt-1">{data.customer.contact_person}</p>}
                                {data.customer.siret && <p className="text-[10px] text-gray-400 mt-1">SIRET: {data.customer.siret}</p>}
                                {data.customer.tva && <p className="text-[10px] text-gray-400">TVA: {data.customer.tva}</p>}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1 md:text-right">
                                <p className="text-[10px] uppercase tracking-[2px] text-gray-400 font-semibold">{t('entities:reports.statement.period', { defaultValue: 'Statement period' })}</p>
                                <p className="font-medium text-[#1A1A1A]">
                                    {filters.start && format(filters.start, 'dd MMM yyyy')} — {filters.end && format(filters.end, 'dd MMM yyyy')}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {t('entities:reports.statement.generated', { defaultValue: 'Generated' })}: {new Date().toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-GB')}
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        <KPICard testId="kpi-opening" label={t('entities:reports.statement.opening', { defaultValue: 'Opening balance' })} value={fmtMoney(s.opening_balance)} />
                        <KPICard testId="kpi-invoiced" accent="info" label={t('entities:reports.statement.invoiced', { defaultValue: 'Invoiced' })} value={fmtMoney(s.invoices_total)} />
                        <KPICard testId="kpi-paid" accent="success" label={t('entities:reports.statement.paid', { defaultValue: 'Paid' })} value={fmtMoney(s.payments_total)} />
                        <KPICard testId="kpi-closing" accent="accent" label={t('entities:reports.statement.closing', { defaultValue: 'Closing balance' })} value={fmtMoney(s.closing_balance)} />
                        <KPICard testId="kpi-overdue" icon={AlertCircle} accent="danger" label={t('entities:reports.statement.overdue', { defaultValue: 'Overdue' })} value={fmtMoney(s.overdue_amount)} />
                    </div>

                    <Card className="border-0 shadow-sm">
                        <CardHeader><CardTitle className="text-sm">{t('entities:reports.statement.ledger', { defaultValue: 'Account ledger' })}</CardTitle></CardHeader>
                        <CardContent>
                            {data.entries.length === 0 ? <ReportEmpty icon={FileText} message={t('entities:reports.empty')} /> : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('entities:reports.columns.date')}</TableHead>
                                                <TableHead>{t('entities:reports.statement.column_type', { defaultValue: 'Type' })}</TableHead>
                                                <TableHead>{t('entities:reports.columns.invoice')}</TableHead>
                                                <TableHead>{t('entities:reports.statement.column_description', { defaultValue: 'Description' })}</TableHead>
                                                <TableHead className="text-right">{t('entities:reports.statement.column_debit', { defaultValue: 'Debit' })}</TableHead>
                                                <TableHead className="text-right">{t('entities:reports.statement.column_credit', { defaultValue: 'Credit' })}</TableHead>
                                                <TableHead className="text-right">{t('entities:reports.statement.column_balance', { defaultValue: 'Balance' })}</TableHead>
                                                <TableHead>{t('entities:reports.statement.column_due_date', { defaultValue: 'Due Date' })}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.entries.map((e, i) => (
                                                <TableRow key={i} className={e.is_overdue ? 'bg-[#E76F51]/5' : ''} data-testid={`ledger-row-${i}`}>
                                                    <TableCell className="text-xs">{e.date}</TableCell>
                                                    <TableCell>
                                                        <Badge className={e.type === 'payment' ? 'bg-[#2A9D8F]/15 text-[#2A9D8F]' : 'bg-[#264653]/10 text-[#264653]'}>
                                                            {e.type === 'payment'
                                                                ? t('entities:reports.statement.type_payment', { defaultValue: 'Payment' })
                                                                : t('entities:reports.statement.type_invoice', { defaultValue: 'Invoice' })}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs">{e.reference}</TableCell>
                                                    <TableCell className="text-sm">{e.description}</TableCell>
                                                    <TableCell className="text-right">{e.debit ? fmtMoney(e.debit) : '—'}</TableCell>
                                                    <TableCell className="text-right text-[#2A9D8F]">{e.credit ? fmtMoney(e.credit) : '—'}</TableCell>
                                                    <TableCell className="text-right font-semibold">{fmtMoney(e.balance)}</TableCell>
                                                    <TableCell className="text-xs">
                                                        {e.due_date && (
                                                            <span className={e.is_overdue ? 'text-[#E76F51] font-semibold' : 'text-gray-500'}>{e.due_date}</span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default CustomerStatementReport;
